import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { AccountInfoContext } from '../../../contexts/AccountInfoContext';
import { NotificationDetailsModel } from '../../../models/notifications';
import { resources } from '../../../resources/resources';
import { notificationService } from '../../../services/notifications';

interface NotificationDetailsState {
  notificationDetails: NotificationDetailsModel;
  isLoading: boolean;
}
export const NotificationDetails = () => {
  const { notificationId } = useParams();
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<NotificationDetailsState>(
    {} as NotificationDetailsState,
  );
  const { accountInfo, setAccountInfo } = useContext(AccountInfoContext);

  const loadDetails = useCallback(
    async (
      notificationId: string | undefined,
      abortController?: AbortController,
    ) => {
      if (_.isNaN(Number(notificationId))) return;
      setState(
        produce(d => {
          d.isLoading = true;
        }),
      );
      const response = await notificationService.getNotificationDetails(
        Number(notificationId),
        {
          signal: abortController?.signal,
        },
      );
      if (abortController?.signal.aborted) return;
      if (!response.hasErrors) {
        if (!response.data.wasRead) {
          setAccountInfo(
            produce(accountInfo, d => {
              d.notificationsCount -= 1;
            }),
          );
        }
        setState(
          produce(d => {
            d.notificationDetails = response.data;
          }),
        );
      }
      setState(
        produce(d => {
          d.isLoading = false;
        }),
      );
    },
    [accountInfo, setAccountInfo],
  );

  useEffect(() => {
    const abortController = new AbortController();

    loadDetails(notificationId, abortController);

    return () => {
      abortController.abort();
    };
  }, [loadDetails, notificationId]);

  return (
    <>
      <div className="m-page-actions">
        <h1>{translate(resources.notifications.title)}</h1>
      </div>
      <div className="m-panel m-notifications-panel">
        <div className="btn-panel-back-wrapper">
          <button
            className="btn btn-panel-back"
            onClick={() => {
              navigate('/notifications');
            }}
          >
            <span className="btn-text">
              {translate(resources.notifications.backToMessages)}
            </span>
          </button>
        </div>
        <Loader loading={state.isLoading} loader={<LoadingSpinner />}>
          <h2>{state.notificationDetails?.title}</h2>
          <div className="m-notification-date">{`${translate(
            resources.notifications.dateSendPrefix,
          )} ${' '} ${moment(
            state.notificationDetails?.dateSend as Date,
          ).format('DD.MM.yyyy, hh:mm')}`}</div>

          <div
            dangerouslySetInnerHTML={{
              __html: state.notificationDetails?.content ?? <p></p>,
            }}
          ></div>
        </Loader>
      </div>
    </>
  );
};
